import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Banner, TextInput } from '../components/ui';

/**
 * Set a new password from the link in a password-reset email.
 *
 * The email points here with ?token=… . We never reveal whether the token
 * is valid until submit, and the backend is the authority on password
 * strength — we only check the two fields match before sending.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('This reset link is invalid or has expired. Request a new one from the sign-in page.');
      return;
    }
    if (password.length < 8) {
      setError('Your new password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await api.post('/auth/password/reset', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiClientError
        ? err.message
        : 'Could not reset your password. The link may have expired — request a new one.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="row gap-12" style={{ marginBottom: 20, justifyContent: 'center' }}>
          <div className="brand-mark" style={{ width: 36, height: 36, fontSize: 17 }}>S</div>
          <span className="brand-name" style={{ fontSize: 19 }}>Salestrendz</span>
        </div>

        <div className="card">
          {done ? (
            <>
              <h1 style={{ fontSize: 21 }}>Password updated</h1>
              <p className="muted small" style={{ marginTop: 5, marginBottom: 20 }}>
                Your password has been changed. You can sign in with it now.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-lg btn-block"
                onClick={() => navigate('/login', { replace: true })}
              >
                Go to sign in
              </button>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: 21 }}>Set a new password</h1>
              <p className="muted small" style={{ marginTop: 5, marginBottom: 20 }}>
                Choose a new password for your account.
              </p>

              <form onSubmit={onSubmit} className="col gap-16">
                {error ? <Banner kind="danger">{error}</Banner> : null}

                <TextInput
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  required
                  value={password}
                  placeholder="At least 8 characters"
                  onChange={(e) => setPassword(e.target.value)}
                />

                <TextInput
                  label="Confirm new password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  placeholder="Re-enter the password"
                  onChange={(e) => setConfirm(e.target.value)}
                />

                <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
                  {busy ? <span className="spinner" /> : null}
                  {busy ? 'Updating' : 'Update password'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="center small faint" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn-link"
            onClick={() => navigate('/login')}
            style={{ background: 'none', border: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Back to sign in
          </button>
        </p>
      </div>
    </div>
  );
}
